import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Formik } from 'formik';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Badge } from 'flowbite-react';
import { Change, Client, Quote } from '../../models';
import {
    loadChange,
    loadClient,
    useAppDispatch,
    useAppSelector,
    RootState,
} from '../../redux';
import { Changes, Quotes } from '../../services';
import {
    Ability, Alert, Button, ChangeListItem, ChangeModal, ClientSelectField, Icon, Loader,
    QuickChangeModal, QuickTrackModal, Reporting,
} from '../../components';
import { clientPath } from '../../utils/clientPath';
import {
    contactDisplayName,
    roleBadgeColor,
    roleLabelKey,
    type ClientContactLike,
} from '../../utils/clientContacts';
import { requesterPath } from '../../utils/requesterPath';

const CLOSED_STATUSES = [
    Change.STATUS_FINISHED,
    Change.STATUS_DEPLOYED,
    Change.STATUS_MERGED,
].join(',');

type CardsPanelProps = {
    title: string;
    count: number;
    items: Change[];
    emptyKey: string;
    accentClass: string;
    onOpen: (change: Change) => void;
};

function CardsPanel({ title, count, items, emptyKey, accentClass, onOpen }: CardsPanelProps) {
    const { t } = useTranslation();

    return (
        <section className="flex min-h-0 flex-col overflow-hidden rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm">
            <div className={`flex items-center justify-between border-b px-4 py-3 ${accentClass}`}>
                <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                    {title}
                </h2>
                <span className="rounded-full bg-white/80 px-2.5 py-0.5 text-xs font-semibold text-gray-700 dark:text-gray-200 tabular-nums">
                    {count}
                </span>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto px-3 py-2" style={{ maxHeight: '28rem' }}>
                {!items.length ? (
                    <p className="px-1 py-6 text-center text-sm text-gray-500 dark:text-gray-400">{t(emptyKey)}</p>
                ) : (
                    <ul className="divide-y divide-gray-100">
                        {items.map((change) => (
                            <li key={change._id} className="py-2">
                                <ChangeListItem
                                    change={change}
                                    showProject
                                    onClick={() => onOpen(change)}
                                />
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </section>
    );
}

type QuotesPanelProps = {
    quotes: Quote[];
    loading: boolean;
};

function QuotesPanel({ quotes, loading }: QuotesPanelProps) {
    const { t } = useTranslation();
    const navigate = useNavigate();

    return (
        <section className="flex min-h-0 flex-col overflow-hidden rounded-xl border border-amber-200 bg-white dark:bg-gray-800 shadow-sm">
            <div className="flex items-center justify-between border-b bg-amber-50/80 px-4 py-3">
                <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                    {t('client_view.quotes')}
                </h2>
                <span className="rounded-full bg-white/80 px-2.5 py-0.5 text-xs font-semibold text-gray-700 dark:text-gray-200 tabular-nums">
                    {loading ? '—' : quotes.length}
                </span>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto px-3 py-2" style={{ maxHeight: '18rem' }}>
                {loading ? (
                    <div className="flex justify-center py-8">
                        <Loader />
                    </div>
                ) : !quotes.length ? (
                    <p className="px-1 py-6 text-center text-sm text-gray-500 dark:text-gray-400">
                        {t('client_view.empty_quotes')}
                    </p>
                ) : (
                    <ul className="divide-y divide-gray-100">
                        {quotes.map((quote) => (
                            <li key={quote._id}>
                                <button
                                    type="button"
                                    onClick={() => navigate(`/quotes/${quote._id}`)}
                                    className="flex w-full items-center gap-3 py-2.5 text-left hover:bg-slate-50 dark:hover:bg-gray-800"
                                >
                                    <div className="min-w-0 flex-1">
                                        <div className="flex flex-wrap items-center gap-2">
                                            <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                                                {quote.slug}
                                            </span>
                                            <Badge color={quote.statusColor()} className="w-fit">
                                                {t(`quotes.status_type.${quote.status}`)}
                                            </Badge>
                                        </div>
                                        <p className="truncate text-sm text-gray-700 dark:text-gray-200">
                                            {quote.name}
                                        </p>
                                        <p className="truncate text-xs text-gray-500 dark:text-gray-400">
                                            {quote.project?.name}
                                        </p>
                                    </div>
                                    <span className="shrink-0 text-sm font-semibold tabular-nums text-gray-900 dark:text-gray-100">
                                        {quote.formattedPrice()}
                                    </span>
                                </button>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </section>
    );
}

type ContactsPanelProps = {
    contacts: ClientContactLike[];
};

function ContactsPanel({ contacts }: ContactsPanelProps) {
    const { t } = useTranslation();

    return (
        <section className="overflow-hidden rounded-xl border border-indigo-200 bg-white dark:bg-gray-800 shadow-sm">
            <div className="flex items-center justify-between border-b border-indigo-100 bg-indigo-50/80 px-4 py-3 dark:border-gray-700 dark:bg-gray-900/40">
                <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                    {t('client_view.contacts')}
                </h2>
                <span className="rounded-full bg-white/80 px-2.5 py-0.5 text-xs font-semibold text-gray-700 dark:bg-gray-800 dark:text-gray-200 tabular-nums">
                    {contacts.length}
                </span>
            </div>
            {!contacts.length ? (
                <p className="px-4 py-6 text-center text-sm text-gray-500 dark:text-gray-400">
                    {t('client_view.empty_contacts')}
                </p>
            ) : (
                <ul className="divide-y divide-gray-100 dark:divide-gray-700">
                    {contacts.map((contact, index) => {
                        const name = contactDisplayName(contact);
                        const email = String(contact.email || '').trim();
                        const phone = String(contact.mobile || contact.tel || '').trim();
                        const roles = (contact.roles || []).filter(Boolean);
                        const key = String(contact.externalId ?? `${email}-${index}`);

                        return (
                            <li key={key} className="flex flex-wrap items-start gap-3 px-4 py-3">
                                <div className="min-w-0 flex-1">
                                    <div className="flex flex-wrap items-center gap-2">
                                        {name ? (
                                            <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                                                {name}
                                            </span>
                                        ) : (
                                            <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
                                                {t('client_view.contact_unnamed')}
                                            </span>
                                        )}
                                        {roles.map((role) => {
                                            const labelKey = roleLabelKey(role);
                                            return (
                                                <Badge
                                                    key={`${key}-${role}`}
                                                    color={roleBadgeColor(role)}
                                                    className="w-fit"
                                                >
                                                    {labelKey ? t(labelKey) : String(role)}
                                                </Badge>
                                            );
                                        })}
                                    </div>
                                    <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-gray-600 dark:text-gray-300">
                                        {email ? (
                                            <Link
                                                to={requesterPath(email)}
                                                className="text-blue-700 hover:underline dark:text-blue-300"
                                            >
                                                {email}
                                            </Link>
                                        ) : null}
                                        {phone ? <span>{phone}</span> : null}
                                    </div>
                                </div>
                            </li>
                        );
                    })}
                </ul>
            )}
        </section>
    );
}

function ClientView() {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const { id } = useParams<{ id?: string }>();

    const { client, loadingOne, loadingOneError } = useAppSelector(
        (state: RootState) => state.clients
    );
    const { change: detailChange } = useAppSelector((state: RootState) => state.changes);
    const { user } = useAppSelector((state: RootState) => state.auth);
    const canReadQuotes = !!user?.can('quote:read');

    const [openChanges, setOpenChanges] = useState<Change[]>([]);
    const [closedChanges, setClosedChanges] = useState<Change[]>([]);
    const [quotes, setQuotes] = useState<Quote[]>([]);
    const [loadingChanges, setLoadingChanges] = useState(false);
    const [loadingQuotes, setLoadingQuotes] = useState(false);
    const [changesError, setChangesError] = useState('');
    const [modalChangeId, setModalChangeId] = useState<string | undefined>();
    const [quickCreateOpen, setQuickCreateOpen] = useState(false);
    const [quickTrackOpen, setQuickTrackOpen] = useState(false);
    const [reportingKey, setReportingKey] = useState(0);

    const activeClient = id && client?._id === id ? client : null;

    const projectsCount = useMemo(() => {
        const ids = new Set<string>();
        [...openChanges, ...closedChanges].forEach((change) => {
            const projectId = change.project?._id || (change as AnyObj).project;
            if (projectId && typeof projectId === 'string')
                ids.add(projectId);
        });
        quotes.forEach((quote) => {
            const projectId = quote.project?._id || (quote as AnyObj).project;
            if (projectId && typeof projectId === 'string')
                ids.add(projectId);
        });
        return ids.size;
    }, [openChanges, closedChanges, quotes]);

    const reloadChanges = useCallback(async () => {
        if (!id) {
            setOpenChanges([]);
            setClosedChanges([]);
            return;
        }
        setLoadingChanges(true);
        setChangesError('');
        try {
            const [openResult, closedResult] = await Promise.all([
                Changes.list({ client: id, notfinished: true }),
                Changes.list({
                    client: id,
                    'progress.status': CLOSED_STATUSES,
                }),
            ]);
            const byUpdatedDesc = (a: Change, b: Change) => {
                const aAt = a.updatedAt ? new Date(a.updatedAt).getTime() : 0;
                const bAt = b.updatedAt ? new Date(b.updatedAt).getTime() : 0;
                return bAt - aAt;
            };
            setOpenChanges((openResult.changes || []).slice().sort(byUpdatedDesc));
            setClosedChanges((closedResult.changes || []).slice().sort(byUpdatedDesc));
        } catch (err: any) {
            setChangesError(err?.message || t('client_view.load_error') || '');
            setOpenChanges([]);
            setClosedChanges([]);
        } finally {
            setLoadingChanges(false);
        }
    }, [id, t]);

    const reloadQuotes = useCallback(async () => {
        if (!id || !canReadQuotes) {
            setQuotes([]);
            return;
        }
        setLoadingQuotes(true);
        try {
            const result = await Quotes.list({ client: id });
            const list = (result.quotes || []).slice().sort(
                (a: Quote, b: Quote) => (Number(b.slug) || 0) - (Number(a.slug) || 0)
            );
            setQuotes(list);
        } catch {
            setQuotes([]);
        } finally {
            setLoadingQuotes(false);
        }
    }, [id, canReadQuotes]);

    useEffect(() => {
        if (id)
            dispatch(loadClient(id));
    }, [dispatch, id]);

    useEffect(() => {
        reloadChanges();
    }, [reloadChanges]);

    useEffect(() => {
        reloadQuotes();
    }, [reloadQuotes]);

    useEffect(() => {
        if (modalChangeId)
            dispatch(loadChange(modalChangeId));
    }, [dispatch, modalChangeId]);

    useEffect(() => {
        const base = t('client_view.title') || 'Client';
        document.title = activeClient ? `${activeClient.name()} · ${base}` : base;
        return () => { document.title = 'Yoda'; };
    }, [activeClient, t]);

    const openChangeModal = (change: Change) => setModalChangeId(change._id);
    const closeChangeModal = () => setModalChangeId(undefined);
    const onRemoveCard = (_removed: AnyObj) => {
        // Soft-delete is handled inside ChangeModal; refresh the list.
        closeChangeModal();
        reloadChanges();
    };

    const onPickClient = (picked?: Client) => {
        if (picked?._id)
            navigate(clientPath(picked._id));
    };

    const clientLoading = !!id && loadingOne === 'pending';
    const clientMissing = !!id && loadingOne === 'succeeded' && !activeClient;
    const clientFailed = !!id && loadingOne === 'failed';
    const kpiCols = canReadQuotes ? 'grid-cols-2 lg:grid-cols-4' : 'grid-cols-3';

    return (
        <Ability can="client:read" redirect="/">
            <div className="ClientView min-h-full bg-slate-50 dark:bg-gray-800">
                <header className="border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-4 py-4 sm:px-6 lg:px-8">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                        <div className="min-w-0">
                            <p className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                                {t('client_view.title')}
                            </p>
                            <h1 className="mt-0.5 flex items-center gap-2 text-2xl font-bold tracking-tight text-gray-900 dark:text-gray-100">
                                <Icon type="client" size={7} className="shrink-0 text-gray-700 dark:text-gray-200" />
                                <span className="truncate">
                                    {activeClient?.company || activeClient?.name() || t('client_view.title')}
                                </span>
                            </h1>
                            {activeClient && (
                                <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-gray-600 dark:text-gray-300">
                                    {activeClient.fullname() && (
                                        <span>{activeClient.fullname()}</span>
                                    )}
                                    {activeClient.email && (
                                        <Link
                                            to={requesterPath(activeClient.email)}
                                            className="text-blue-700 hover:underline"
                                        >
                                            {activeClient.email}
                                        </Link>
                                    )}
                                    {activeClient.id != null && (
                                        <span className="rounded-md bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 text-xs font-medium text-gray-700 dark:text-gray-200">
                                            {t('client_view.zone_client_id')} {activeClient.id}
                                        </span>
                                    )}
                                </div>
                            )}
                        </div>
                        {activeClient && (
                            <div className="flex flex-wrap items-center gap-2">
                                <Button
                                    color="primary"
                                    icon="plus"
                                    title={t('client_view.new_change') || ''}
                                    onClick={() => setQuickCreateOpen(true)}
                                />
                                <Ability can="track:create">
                                    <Button
                                        color="secondary"
                                        icon="timer"
                                        title={t('client_view.new_track') || ''}
                                        onClick={() => setQuickTrackOpen(true)}
                                    />
                                </Ability>
                                <Link
                                    to={clientPath()}
                                    className="inline-flex items-center gap-1.5 rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-slate-50"
                                >
                                    <Icon type="search" size={4} />
                                    {t('client_view.change_client')}
                                </Link>
                            </div>
                        )}
                    </div>
                </header>

                <main className="px-4 py-5 sm:px-6 lg:px-8">
                    {!id && (
                        <div className="mx-auto mb-8 mt-16 w-full max-w-xl">
                            <p className="mb-2 text-center text-sm font-medium text-gray-600 dark:text-gray-300">
                                {t('client_view.pick_hint')}
                            </p>
                            <Formik
                                initialValues={{ client: undefined }}
                                enableReinitialize
                                onSubmit={() => {}}
                            >
                                <ClientSelectField
                                    name="client"
                                    placeholder={t('client_view.search_placeholder') || ''}
                                    onChange={onPickClient}
                                    allowClear={false}
                                    autoFocus
                                />
                            </Formik>
                        </div>
                    )}

                    {!id ? null : clientLoading ? (
                        <div className="mt-8 flex justify-center">
                            <Loader />
                        </div>
                    ) : clientFailed || clientMissing ? (
                        <Alert
                            title={loadingOneError || t('client_view.not_found') || ''}
                            icon="warning"
                            color="failure"
                        />
                    ) : activeClient ? (
                        <div className="space-y-4">
                            {changesError && (
                                <Alert title={changesError} icon="warning" color="failure" />
                            )}

                            <div className={`grid gap-3 ${kpiCols}`}>
                                <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 shadow-sm">
                                    <p className="text-xs font-medium uppercase tracking-wide text-emerald-800/80">
                                        {t('client_view.open_changes')}
                                    </p>
                                    <p className="mt-1 text-3xl font-bold tabular-nums text-emerald-900">
                                        {loadingChanges ? '—' : openChanges.length}
                                    </p>
                                </div>
                                <div className="rounded-xl border border-slate-200 bg-white dark:bg-gray-800 px-4 py-3 shadow-sm">
                                    <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
                                        {t('client_view.closed_changes')}
                                    </p>
                                    <p className="mt-1 text-3xl font-bold tabular-nums text-slate-900">
                                        {loadingChanges ? '—' : closedChanges.length}
                                    </p>
                                </div>
                                <div className="rounded-xl border border-indigo-200 bg-indigo-50 dark:bg-indigo-950/30 px-4 py-3 shadow-sm">
                                    <p className="text-xs font-medium uppercase tracking-wide text-indigo-800/80">
                                        {t('client_view.projects')}
                                    </p>
                                    <p className="mt-1 text-3xl font-bold tabular-nums text-indigo-900">
                                        {loadingChanges || loadingQuotes ? '—' : projectsCount}
                                    </p>
                                </div>
                                {canReadQuotes && (
                                    <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 shadow-sm">
                                        <p className="text-xs font-medium uppercase tracking-wide text-amber-800/80">
                                            {t('client_view.quotes')}
                                        </p>
                                        <p className="mt-1 text-3xl font-bold tabular-nums text-amber-900">
                                            {loadingQuotes ? '—' : quotes.length}
                                        </p>
                                    </div>
                                )}
                            </div>

                            <ContactsPanel contacts={activeClient.contacts || []} />

                            {loadingChanges ? (
                                <div className="flex justify-center py-12">
                                    <Loader />
                                </div>
                            ) : (
                                <div className="grid grid-cols-1 gap-4 xl:grid-cols-2 xl:items-stretch">
                                    <CardsPanel
                                        title={t('client_view.open_changes') || ''}
                                        count={openChanges.length}
                                        items={openChanges}
                                        emptyKey="client_view.empty_open"
                                        accentClass="bg-emerald-50/80"
                                        onOpen={openChangeModal}
                                    />
                                    <CardsPanel
                                        title={t('client_view.closed_changes') || ''}
                                        count={closedChanges.length}
                                        items={closedChanges}
                                        emptyKey="client_view.empty_closed"
                                        accentClass="bg-slate-50 dark:bg-gray-800"
                                        onOpen={openChangeModal}
                                    />
                                </div>
                            )}

                            {canReadQuotes && (
                                <QuotesPanel quotes={quotes} loading={loadingQuotes} />
                            )}

                            <section className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4 shadow-sm">
                                <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                                    <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                                        {t('client_view.interventions')}
                                    </h2>
                                    <span className="text-xs text-gray-500 dark:text-gray-400">
                                        {t('client_view.interventions_period')}
                                    </span>
                                </div>
                                <Reporting
                                    key={`${activeClient._id}-${reportingKey}`}
                                    client={activeClient}
                                    from={moment().startOf('year')}
                                    to={moment().endOf('day')}
                                    tabs={['tracks', 'dashboard']}
                                    onOpenChange={openChangeModal}
                                />
                            </section>
                        </div>
                    ) : null}
                </main>

                {detailChange && detailChange._id === modalChangeId && detailChange.project?.name && (
                    <ChangeModal
                        project={detailChange.project}
                        change={detailChange}
                        show={true}
                        showProjectName={true}
                        onClose={closeChangeModal}
                        onRemove={onRemoveCard}
                    />
                )}

                {activeClient && (
                    <QuickChangeModal
                        show={quickCreateOpen}
                        initialClient={activeClient}
                        onClose={() => setQuickCreateOpen(false)}
                        onAdded={() => {
                            setQuickCreateOpen(false);
                            reloadChanges();
                        }}
                    />
                )}

                {activeClient && (
                    <QuickTrackModal
                        show={quickTrackOpen}
                        initialClient={activeClient}
                        onClose={() => setQuickTrackOpen(false)}
                        onAdded={() => {
                            setQuickTrackOpen(false);
                            setReportingKey((key) => key + 1);
                        }}
                    />
                )}
            </div>
        </Ability>
    );
}

export default ClientView;
